import { create } from "zustand";

type CurrencyStore = {
  currency: string;
  rates: Record<string, number>;
  lastFetched: number | null;
  isFetching: boolean;
  setCurrency: (currency: string) => void;
  fetchRates: (force?: boolean) => Promise<void>;
};

const defaultCurrency =
  typeof window !== "undefined"
    ? localStorage.getItem("selectedCurrency") ||
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("currency="))
        ?.split("=")[1] ||
      "USD"
    : "USD";

// try to hydrate cached rates / timestamp if we’re in the browser
const cachedRates =
  typeof window !== "undefined" && localStorage.getItem("currencyRates")
    ? (JSON.parse(localStorage.getItem("currencyRates") as string) as Record<string, number>)
    : { USD: 1 };

const cachedFetchedAt =
  typeof window !== "undefined" && localStorage.getItem("currencyRatesLastFetched")
    ? Number(localStorage.getItem("currencyRatesLastFetched"))
    : null;

const TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

export const useCurrencyStore = create<CurrencyStore>((set, get) => ({
  currency: defaultCurrency,
  rates: cachedRates,
  lastFetched: cachedFetchedAt,
  isFetching: false,

  setCurrency: (currency) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("selectedCurrency", currency);
      document.cookie = `currency=${currency}; path=/; max-age=31536000`; // 1 year
    }
    set({ currency });
  },

  fetchRates: async (force = false) => {
    const { lastFetched, isFetching } = get();
    const isFresh = lastFetched != null && Date.now() - lastFetched < TTL_MS;

    // skip if we already have fresh data or a request in flight
    if (!force && (isFresh || isFetching)) return;

    try {
      set({ isFetching: true });
      const res = await fetch("/api/currency/rates");
      const data = await res.json();
      if (!data?.rates) throw new Error("No rates returned");

      const now = Date.now();
      set({ rates: data.rates, lastFetched: now, isFetching: false });

      if (typeof window !== "undefined") {
        localStorage.setItem("currencyRates", JSON.stringify(data.rates));
        localStorage.setItem("currencyRatesLastFetched", String(now));
      }
    } catch (err) {
      set({ isFetching: false });
    }
  },
}));
